import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Brand } from '../App'
import { getAdminSession, getDashboard, login, logout } from '../lib/api'
import type { DashboardData, DownloadRecord } from '../lib/api'
import { exportDownloadsToCsv, exportDownloadsToXlsx } from '../lib/exportUtils'
import { CATALOGUE } from '../../shared/catalogue'
import type { Book } from '../../shared/types'

const number = new Intl.NumberFormat('pt-BR')
const dateTime = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getInitialDates(days = 30): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - days)
  return {
    start: formatIsoDate(start),
    end: formatIsoDate(end),
  }
}

export function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null)
  const [activePreset, setActivePreset] = useState<'7' | '30' | '90' | 'all' | 'custom'>('30')
  const [dates, setDates] = useState(() => getInitialDates(30))
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    getAdminSession()
      .then((session) => setAuthenticated(session.authenticated))
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Não foi possível verificar a sessão.')
        setAuthenticated(false)
      })
  }, [])

  useEffect(() => {
    if (!authenticated) return
    setIsLoading(true)

    const periodParam = activePreset === 'all' ? 'all' : 'custom'
    const startParam = activePreset === 'all' ? undefined : dates.start
    const endParam = activePreset === 'all' ? undefined : dates.end

    getDashboard(periodParam, startParam, endParam)
      .then((data) => {
        setDashboard(data)
        setError('')
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Não foi possível carregar os relatórios.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [authenticated, activePreset, dates.start, dates.end])

  const handleLogout = async () => {
    await logout()
    setDashboard(null)
    setAuthenticated(false)
  }

  const handlePresetClick = (preset: '7' | '30' | '90' | 'all') => {
    setActivePreset(preset)
    if (preset === 'all') {
      setDates({ start: '', end: '' })
    } else {
      setDates(getInitialDates(Number(preset)))
    }
  }

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    setActivePreset('custom')
    setDates((prev) => ({ ...prev, [field]: value }))
  }

  if (authenticated === null) {
    return <main className="admin-loading">Verificando acesso...</main>
  }

  if (!authenticated) {
    return <AdminLogin onAuthenticated={() => setAuthenticated(true)} initialError={error} />
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <Brand />
        <div className="admin-header-actions">
          <Link to="/" className="header-link">Biblioteca</Link>
          <button className="button button-secondary" type="button" onClick={handleLogout}>Sair</button>
        </div>
      </header>
      <main className="admin-main">
        <div className="admin-title-row">
          <div>
            <span className="eyebrow">Área restrita</span>
            <h1>Downloads e relatórios</h1>
          </div>

          <div className="date-range-control" role="group" aria-label="Seleção de período por datas">
            <span className="date-range-label">Período:</span>
            <div className="date-quick-presets">
              <button
                type="button"
                className={`preset-btn ${activePreset === '7' ? 'active' : ''}`}
                onClick={() => handlePresetClick('7')}
              >
                7d
              </button>
              <button
                type="button"
                className={`preset-btn ${activePreset === '30' ? 'active' : ''}`}
                onClick={() => handlePresetClick('30')}
              >
                30d
              </button>
              <button
                type="button"
                className={`preset-btn ${activePreset === '90' ? 'active' : ''}`}
                onClick={() => handlePresetClick('90')}
              >
                90d
              </button>
              <button
                type="button"
                className={`preset-btn ${activePreset === 'all' ? 'active' : ''}`}
                onClick={() => handlePresetClick('all')}
              >
                Tudo
              </button>
            </div>
            <div className="date-input-group">
              <input
                type="date"
                className="date-input"
                value={dates.start}
                onChange={(e) => handleDateChange('start', e.target.value)}
                aria-label="Data inicial"
              />
              <span>até</span>
              <input
                type="date"
                className="date-input"
                value={dates.end}
                onChange={(e) => handleDateChange('end', e.target.value)}
                aria-label="Data final"
              />
            </div>
          </div>
        </div>

        {error ? <div className="status-message" role="alert">{error}</div> : null}
        {isLoading && !dashboard ? <div className="status-message">Atualizando indicadores...</div> : null}
        {dashboard && <Dashboard dashboard={dashboard} />}
      </main>
    </div>
  )
}

function AdminLogin({ onAuthenticated, initialError }: { onAuthenticated: () => void; initialError: string }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(initialError)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await login(username, password)
      onAuthenticated()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível entrar.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="login-page">
      <div className="login-brand"><Brand /></div>
      <section className="login-card" aria-labelledby="login-title">
        <span className="eyebrow">Área restrita</span>
        <h1 id="login-title">Acesso aos relatórios</h1>
        <p>Use as credenciais administrativas configuradas no ambiente Cloudflare.</p>
        <form onSubmit={handleSubmit}>
          <label className="field">
            <span>Usuário</span>
            <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" required />
          </label>
          <label className="field">
            <span>Senha</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button button-primary button-full" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </section>
    </main>
  )
}

const ITEMS_PER_PAGE = 20

function Dashboard({ dashboard }: { dashboard: DashboardData }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedBookIds, setSelectedBookIds] = useState<number[]>([])
  const [formatFilter, setFormatFilter] = useState<'all' | 'pdf' | 'epub'>('all')
  const [profileFilter, setProfileFilter] = useState<'all' | 'business' | 'individual'>('all')
  const [currentPage, setCurrentPage] = useState(1)

  // Unifica dados de downloads disponíveis
  const allDownloads: DownloadRecord[] = useMemo(() => {
    return dashboard.downloads ?? dashboard.recent ?? []
  }, [dashboard.downloads, dashboard.recent])

  // Filtro completo em tempo real
  const filteredDownloads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return allDownloads.filter((item) => {
      // Filtro por texto: Leitor, Telefone ou E-mail
      if (query) {
        const nameMatch = item.name.toLowerCase().includes(query)
        const emailMatch = item.email.toLowerCase().includes(query)
        const phoneMatch = item.phone ? item.phone.toLowerCase().includes(query) : false
        if (!nameMatch && !emailMatch && !phoneMatch) return false
      }

      // Filtro por Obras selecionadas
      if (selectedBookIds.length > 0) {
        if (!item.bookId || !selectedBookIds.includes(item.bookId)) {
          // Fallback por comparação de título caso bookId venha indefinido
          const matchedCatalogueBook = CATALOGUE.find((b: Book) => b.title === item.bookTitle)
          if (!matchedCatalogueBook || !selectedBookIds.includes(matchedCatalogueBook.id)) {
            return false
          }
        }
      }

      // Filtro por Formato
      if (formatFilter !== 'all' && item.format.toLowerCase() !== formatFilter) {
        return false
      }

      // Filtro por Perfil
      if (profileFilter === 'business' && !item.isBusinessOwner) return false
      if (profileFilter === 'individual' && item.isBusinessOwner) return false

      return true
    })
  }, [allDownloads, searchQuery, selectedBookIds, formatFilter, profileFilter])

  // Reset de página quando filtros são alterados
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedBookIds, formatFilter, profileFilter])

  // Paginação
  const totalPages = Math.max(1, Math.ceil(filteredDownloads.length / ITEMS_PER_PAGE))
  const paginatedDownloads = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredDownloads.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredDownloads, currentPage])

  const toggleBookFilter = (bookId: number) => {
    setSelectedBookIds((prev) =>
      prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId],
    )
  }

  const clearBookFilters = () => {
    setSelectedBookIds([])
  }

  const handleExportCsv = () => {
    exportDownloadsToCsv(
      filteredDownloads,
      `downloads-fecomercio-${new Date().toISOString().slice(0, 10)}.csv`,
    )
  }

  const handleExportXlsx = () => {
    exportDownloadsToXlsx(
      filteredDownloads,
      `downloads-fecomercio-${new Date().toISOString().slice(0, 10)}.xlsx`,
    )
  }

  return (
    <div className="dashboard-content">
      <section className="metric-grid" aria-label="Indicadores do período">
        <Metric label="Downloads" value={dashboard.summary.totalDownloads} />
        <Metric label="Leitores únicos" value={dashboard.summary.uniqueReaders} />
        <Metric label="Empresários" value={dashboard.summary.businessOwners} />
        <Metric label="Inscritos Newsletter" value={dashboard.summary.newsletterOptIns} />
        <Metric label="Arquivos PDF" value={dashboard.summary.pdfDownloads} />
        <Metric label="Arquivos EPUB" value={dashboard.summary.epubDownloads} />
      </section>

      <section className="dashboard-panel">
        <div className="panel-header">
          <h2>Downloads por obra</h2>
          <span>Atualizado em {dateTime.format(new Date(dashboard.generatedAt))}</span>
        </div>
        <div className="book-report-list">
          {dashboard.byBook.map((book) => {
            const width = dashboard.summary.totalDownloads
              ? Math.max((book.downloads / dashboard.summary.totalDownloads) * 100, book.downloads ? 3 : 0)
              : 0
            return (
              <div className="book-report-item" key={book.id}>
                <div><span>Obra {String(book.id).padStart(2, '0')}</span><strong>{book.title}</strong></div>
                <div className="bar-track" aria-label={`${book.downloads} downloads`}><span style={{ width: `${width}%` }} /></div>
                <b>{number.format(book.downloads)}</b>
              </div>
            )
          })}
        </div>
      </section>

      <section className="dashboard-panel">
        <div className="panel-header">
          <div>
            <h2>Downloads</h2>
            <span>
              {filteredDownloads.length} {filteredDownloads.length === 1 ? 'registro encontrado' : 'registros encontrados'}
              {filteredDownloads.length !== allDownloads.length ? ` (de ${allDownloads.length} no período)` : ''}
            </span>
          </div>

          <div className="export-group">
            <button
              type="button"
              className="button-export"
              onClick={handleExportCsv}
              title="Exportar todos os registros filtrados em CSV"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              CSV Filtrado ({filteredDownloads.length})
            </button>
            <button
              type="button"
              className="button-export button-export-excel"
              onClick={handleExportXlsx}
              title="Exportar todos os registros filtrados em planilha Excel (.xlsx)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
              Excel (.xlsx)
            </button>
          </div>
        </div>

        <div className="table-filter-bar">
          <div className="search-row">
            <div className="search-box">
              <span className="search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input
                type="search"
                className="search-input"
                placeholder="Buscar por nome do leitor, telefone ou e-mail..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="search-filters-secondary">
              <select
                className="filter-select"
                value={formatFilter}
                onChange={(e) => setFormatFilter(e.target.value as 'all' | 'pdf' | 'epub')}
                aria-label="Filtrar por formato de download"
              >
                <option value="all">Todos os formatos</option>
                <option value="pdf">Apenas PDF</option>
                <option value="epub">Apenas EPUB</option>
              </select>

              <select
                className="filter-select"
                value={profileFilter}
                onChange={(e) => setProfileFilter(e.target.value as 'all' | 'business' | 'individual')}
                aria-label="Filtrar por perfil"
              >
                <option value="all">Todos os perfis</option>
                <option value="business">Empresários</option>
                <option value="individual">Não empresários</option>
              </select>
            </div>
          </div>

          <div className="books-filter-group">
            <span className="books-filter-title">Filtrar por Obra:</span>
            <div className="books-chips" role="group" aria-label="Filtro por obras">
              <button
                type="button"
                className={`book-chip book-chip-all ${selectedBookIds.length === 0 ? 'selected' : ''}`}
                onClick={clearBookFilters}
              >
                Todas as 6 obras
              </button>
              {CATALOGUE.map((book: Book) => {
                const isSelected = selectedBookIds.includes(book.id)
                return (
                  <button
                    key={book.id}
                    type="button"
                    className={`book-chip ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleBookFilter(book.id)}
                  >
                    Obra {String(book.id).padStart(2, '0')} - {book.title.slice(0, 32)}...
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {filteredDownloads.length === 0 ? (
          <p className="empty-state">
            Nenhum download encontrado com os filtros e datas selecionados.
          </p>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Leitor</th>
                    <th>Telefone</th>
                    <th>Perfil</th>
                    <th>Informativos</th>
                    <th>Obra</th>
                    <th>Formato</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDownloads.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.name}</strong>
                        <small>{item.email}</small>
                      </td>
                      <td className="phone-cell">{item.phone || '-'}</td>
                      <td>
                        <span className={`status-pill ${item.isBusinessOwner ? 'pill-business' : 'pill-individual'}`}>
                          {item.isBusinessOwner ? 'Empresário' : 'Não empresário'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-pill ${item.optInNewsletter ? 'pill-optin' : 'pill-optout'}`}>
                          {item.optInNewsletter ? 'Autorizou' : 'Não'}
                        </span>
                      </td>
                      <td>{item.bookTitle}</td>
                      <td>
                        <span className="format-tag">{item.format.toUpperCase()}</span>
                      </td>
                      <td>{dateTime.format(new Date(`${item.downloadedAt}Z`))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination-row">
                <span className="pagination-info">
                  Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, filteredDownloads.length)} de {filteredDownloads.length} resultados (20 por página)
                </span>
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <span className="pagination-current">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    type="button"
                    className="pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <article className="metric">
      <span>{label}</span>
      <strong>{number.format(value)}</strong>
    </article>
  )
}
